import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Page = styled.div`
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const Title = styled.div`
  h1 {
    margin: 0 0 6px;
    font-size: 1.75rem;
    letter-spacing: -0.04em;
  }
  p {
    margin: 0;
    color: rgba(255,255,255,.62);
    font-size: .92rem;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  border: 1px solid rgba(139,210,244,.28);
  background: ${(p) => p.$primary ? 'linear-gradient(135deg,#01BCD6,#19AA9E)' : 'rgba(255,255,255,.06)'};
  color: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform .15s ease, background .15s ease;
  &:hover {
    transform: translateY(-1px);
    background: ${(p) => p.$primary ? 'linear-gradient(135deg,#0ac8e2,#20bba9)' : 'rgba(255,255,255,.1)'};
  }
  &:disabled {
    opacity: .5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const SummaryCard = styled.div`
  background: linear-gradient(180deg, rgba(15,38,59,.72), rgba(10,25,41,.82));
  border: 1px solid rgba(139,210,244,.12);
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 10px 28px rgba(0,0,0,.16);
`;

const SummaryLabel = styled.div`
  color: rgba(255,255,255,.56);
  font-size: .78rem;
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  font-size: 1.45rem;
  font-weight: 800;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const Search = styled.input`
  flex: 1;
  min-width: 220px;
  border: 1px solid rgba(139,210,244,.16);
  background: rgba(255,255,255,.06);
  color: #fff;
  border-radius: 10px;
  padding: 11px 12px;
  outline: none;
  &::placeholder { color: rgba(255,255,255,.38); }
`;

const Select = styled.select`
  border: 1px solid rgba(139,210,244,.16);
  background: #102338;
  color: #fff;
  border-radius: 10px;
  padding: 11px 12px;
  outline: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`;

const ServiceCard = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(15,38,59,.72), rgba(10,25,41,.86));
  border: 1px solid ${(p) => p.$matched ? 'rgba(34,197,94,.38)' : 'rgba(139,210,244,.12)'};
  border-left: 4px solid ${(p) => p.$matched ? '#22c55e' : '#64748b'};
  border-radius: 14px;
  padding: 14px;
  min-height: 172px;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const ServiceName = styled.div`
  font-weight: 800;
  font-size: 1rem;
  line-height: 1.2;
`;

const ServiceCode = styled.div`
  color: rgba(255,255,255,.45);
  font-size: .74rem;
  margin-top: 3px;
  font-family: Consolas, monospace;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  height: fit-content;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: .7rem;
  font-weight: 800;
  color: ${(p) => p.$color || '#8BD2F4'};
  background: ${(p) => p.$bg || 'rgba(139,210,244,.12)'};
  border: 1px solid ${(p) => p.$border || 'rgba(139,210,244,.18)'};
`;

const Meta = styled.div`
  display: grid;
  gap: 6px;
  margin: 10px 0 12px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  color: rgba(255,255,255,.7);
  font-size: .8rem;
  min-width: 0;
  span:first-child {
    color: rgba(255,255,255,.42);
    width: 54px;
    flex: 0 0 auto;
  }
  span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const InstanceList = styled.div`
  border-top: 1px dashed rgba(255,255,255,.08);
  padding-top: 10px;
  display: grid;
  gap: 6px;
`;

const Instance = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: .78rem;
  color: rgba(255,255,255,.72);
  background: rgba(255,255,255,.04);
  border-radius: 8px;
  padding: 7px 8px;
`;

const Empty = styled.div`
  border: 1px dashed rgba(139,210,244,.18);
  background: rgba(255,255,255,.04);
  border-radius: 14px;
  padding: 26px;
  text-align: center;
  color: rgba(255,255,255,.62);
`;

const ErrorBox = styled.div`
  background: rgba(248,113,113,.12);
  border: 1px solid rgba(248,113,113,.28);
  color: #fecaca;
  padding: 12px;
  border-radius: 12px;
  margin-bottom: 14px;
`;

function normalizeScan(scan) {
  const local = scan?.local?.instances || [];
  const pm2 = (scan?.pm2 || []).flatMap((entry) => entry.processes || []);
  return [...local, ...pm2];
}

function statusBadge(instances) {
  if (!instances.length) {
    return { label: '미탐지', color: '#cbd5e1', bg: 'rgba(148,163,184,.12)', border: 'rgba(148,163,184,.2)' };
  }
  if (instances.some((i) => ['errored', 'stopped', 'error'].includes(i.status))) {
    return { label: '주의', color: '#fca5a5', bg: 'rgba(248,113,113,.12)', border: 'rgba(248,113,113,.26)' };
  }
  return { label: '감지됨', color: '#86efac', bg: 'rgba(34,197,94,.12)', border: 'rgba(34,197,94,.24)' };
}

export default function AdminSystems() {
  const [overview, setOverview] = useState(null);
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const loadOverview = useCallback(async () => {
    const res = await fetch('/api/admin/systems', { credentials: 'include' });
    const data = await res.json();
    if (!res.ok || data.success === false) throw new Error(data.message || '시스템 목록 조회 실패');
    setOverview(data);
  }, []);

  const runScan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await fetch('/api/admin/systems/scan', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || '시스템 스캔 실패');
      setScan(data);
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadOverview();
        if (alive) await runScan();
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [loadOverview, runScan]);

  const services = useMemo(() => overview?.services || [], [overview]);
  const instances = useMemo(() => normalizeScan(scan), [scan]);
  const byService = useMemo(() => {
    const map = new Map();
    for (const inst of instances) {
      const key = inst.service_code || '_unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inst);
    }
    return map;
  }, [instances]);

  const categories = useMemo(() => {
    return ['all', ...Array.from(new Set(services.map((s) => s.category).filter(Boolean))).sort()];
  }, [services]);

  const filtered = services.filter((svc) => {
    const q = query.trim().toLowerCase();
    const matchQuery = !q || [svc.name, svc.service_code, svc.description, svc.repository_path, svc.production_url]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
    const matchCategory = category === 'all' || svc.category === category;
    return matchQuery && matchCategory;
  });

  const matchedCount = services.filter((s) => (byService.get(s.service_code) || []).length > 0).length;
  const alertCount = instances.filter((i) => ['errored', 'stopped', 'error'].includes(i.status)).length;

  return (
    <Page>
      <Header>
        <Title>
          <h1>시스템 현황</h1>
          <p>앤츠네스트 전체 서비스가 어느 서버/포트/프로세스에서 움직이는지 읽기 전용으로 확인합니다.</p>
        </Title>
        <Actions>
          <Button onClick={loadOverview} disabled={loading}>목록 새로고침</Button>
          <Button $primary onClick={runScan} disabled={scanning}>{scanning ? '스캔 중...' : '프로세스 스캔'}</Button>
        </Actions>
      </Header>

      {error && <ErrorBox>{error}</ErrorBox>}

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>등록 서비스</SummaryLabel>
          <SummaryValue>{services.length}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>감지된 서비스</SummaryLabel>
          <SummaryValue>{matchedCount}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>실행 인스턴스</SummaryLabel>
          <SummaryValue>{instances.length}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>주의 프로세스</SummaryLabel>
          <SummaryValue>{alertCount}</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      <FilterBar>
        <Search
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="서비스명, 코드, 경로, 도메인 검색"
        />
        <Select value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <option key={c} value={c}>{c === 'all' ? '전체 분류' : c}</option>
          ))}
        </Select>
      </FilterBar>

      {loading ? (
        <Empty>시스템 정보를 불러오는 중입니다.</Empty>
      ) : filtered.length === 0 ? (
        <Empty>표시할 서비스가 없습니다.</Empty>
      ) : (
        <Grid>
          {filtered.map((svc) => {
            const svcInstances = byService.get(svc.service_code) || [];
            const badge = statusBadge(svcInstances);
            return (
              <ServiceCard key={svc.service_code} $matched={svcInstances.length > 0}>
                <CardTop>
                  <div>
                    <ServiceName>{svc.name}</ServiceName>
                    <ServiceCode>{svc.service_code}</ServiceCode>
                  </div>
                  <Badge $color={badge.color} $bg={badge.bg} $border={badge.border}>{badge.label}</Badge>
                </CardTop>
                <Meta>
                  <MetaRow><span>분류</span><span>{svc.category || '-'}</span></MetaRow>
                  <MetaRow><span>서버</span><span>{svc.default_server_id || '-'}</span></MetaRow>
                  <MetaRow><span>포트</span><span>{svc.default_port || '-'}</span></MetaRow>
                  <MetaRow><span>경로</span><span>{svc.repository_path || '-'}</span></MetaRow>
                  <MetaRow><span>URL</span><span>{svc.production_url || svc.health_url || '-'}</span></MetaRow>
                </Meta>
                <InstanceList>
                  {svcInstances.length === 0 ? (
                    <Instance><span>실행 인스턴스 미탐지</span><span>read-only</span></Instance>
                  ) : svcInstances.slice(0, 4).map((inst, idx) => (
                    <Instance key={`${svc.service_code}-${idx}-${inst.pid}-${inst.port}`}>
                      <span>{inst.server_id || 'root'} · {inst.process_name || inst.source}</span>
                      <span>{inst.port ? `:${inst.port}` : inst.status}</span>
                    </Instance>
                  ))}
                </InstanceList>
              </ServiceCard>
            );
          })}
        </Grid>
      )}
    </Page>
  );
}
